import { CATEGORIES, type TastingStatus } from "../types";

export type InventoryPathname = "/" | "/epuises";
export type TastingFilter = "" | TastingStatus;

export interface InventoryFilters {
  query: string;
  category: string;
  location: string;
  tasting: TastingFilter;
  region: string;
  coravin: boolean;
}

export const DRINK_NOW_STATUS: TastingStatus = "À boire dès maintenant";

export const EMPTY_INVENTORY_FILTERS: InventoryFilters = {
  query: "",
  category: "",
  location: "",
  tasting: "",
  region: "",
  coravin: false,
};

const TASTING_FILTERS = new Set<TastingFilter>([
  "",
  DRINK_NOW_STATUS,
  "À garder",
  "Non renseigné",
]);

function compatibleFilters(
  pathname: InventoryPathname,
  filters: InventoryFilters,
): InventoryFilters {
  if (pathname !== "/epuises") return filters;
  return {
    ...filters,
    location: "",
    coravin: false,
    tasting: filters.tasting === DRINK_NOW_STATUS ? "" : filters.tasting,
  };
}

export function inventoryFiltersFromLocation(
  pathname: InventoryPathname,
  search: string,
): InventoryFilters {
  const params = new URLSearchParams(search);
  const category = params.get("categorie") ?? "";
  const tasting = params.get("degustation") ?? "";

  return compatibleFilters(pathname, {
    query: params.get("q") ?? "",
    category: CATEGORIES.includes(category as (typeof CATEGORIES)[number])
      ? category
      : "",
    location: params.get("emplacement") ?? "",
    tasting: TASTING_FILTERS.has(tasting as TastingFilter)
      ? (tasting as TastingFilter)
      : "",
    region: params.get("region") ?? "",
    coravin: params.get("coravin") === "1",
  });
}

export function inventoryPath(
  pathname: InventoryPathname,
  filters: InventoryFilters,
): string {
  const compatible = compatibleFilters(pathname, filters);
  const params = new URLSearchParams();
  if (compatible.query) params.set("q", compatible.query);
  if (compatible.category) params.set("categorie", compatible.category);
  if (compatible.location) params.set("emplacement", compatible.location);
  if (compatible.tasting) params.set("degustation", compatible.tasting);
  if (compatible.region) params.set("region", compatible.region);
  if (compatible.coravin) params.set("coravin", "1");
  const search = params.toString();
  return `${pathname}${search ? `?${search}` : ""}`;
}

export function inventoryReturnPath(search: string): string {
  const rawReturn = new URLSearchParams(search).get("retour");
  if (!rawReturn) return "/";

  try {
    const base = new URL(window.location.origin);
    const target = new URL(rawReturn, base);
    if (target.origin !== base.origin) return "/";
    if (target.pathname !== "/" && target.pathname !== "/epuises") return "/";
    const pathname = target.pathname as InventoryPathname;
    return inventoryPath(
      pathname,
      inventoryFiltersFromLocation(pathname, target.search),
    );
  } catch {
    return "/";
  }
}

export function withInventoryReturn(path: string, returnTo: string): string {
  if (returnTo === "/") return path;
  const params = new URLSearchParams({ retour: returnTo });
  return `${path}?${params.toString()}`;
}
